/**
 * FloatingWidget - フローティングチャットウィジェット
 *
 * 右下（または左下）に固定表示されるチャットウィジェット。
 * トグルボタンで開閉可能。モード切替・最小化機能付き。
 */

import { useState, useCallback } from "react";
import { ChevronDown, X } from "lucide-react";
import { WidgetChatContainer } from "./WidgetChatContainer";
import { cn } from "../../utils";
import { ShirobonAIIcon } from "../../components/ShirobonAIIcon";

type Mode = "medical_fee" | "facility_standard";

export interface FloatingWidgetProps {
	/** API endpoint URL */
	apiUrl: string;
	/** User ID for session tracking */
	userId: string;
	/** Initial chat mode */
	mode?: Mode;
	/** Widget position */
	position?: "bottom-right" | "bottom-left";
	/** z-index for the widget */
	zIndex?: number;
	/** Whether to open the widget by default */
	defaultOpen?: boolean;
}

type PersistedUiStateV1 = {
	v: 1;
	isOpen: boolean;
	hasInitialized: boolean;
	currentMode: Mode;
};

const detectPageModeHint = (): Mode => {
	if (typeof window === "undefined") return "medical_fee";
	const href = window.location?.href ?? "";
	// Facility standard pages live under /standard/ (incl. /standard/latest/...)
	if (href.includes("/standard/")) return "facility_standard";
	return "medical_fee";
};

// UI state is shared across site navigation so the widget stays open/closed consistently.
const uiStorageKey = (userId: string) => `shirobon_ai_widget_ui:${userId}`;
const chatStorageKey = (userId: string, mode: Mode) => `shirobon_ai_widget_chat:${userId}:${mode}`;

const safeLoadJson = <T,>(key: string): T | null => {
	if (typeof window === "undefined") return null;
	try {
		const raw = window.localStorage.getItem(key);
		if (!raw) return null;
		return JSON.parse(raw) as T;
	} catch {
		return null;
	}
};

const safeSaveJson = (key: string, value: unknown) => {
	if (typeof window === "undefined") return;
	try {
		window.localStorage.setItem(key, JSON.stringify(value));
	} catch {
		// ignore (storage full / blocked)
	}
};

const clearPersistedState = (userId: string) => {
	if (typeof window === "undefined") return;
	try {
		window.localStorage.removeItem(uiStorageKey(userId));
		window.localStorage.removeItem(chatStorageKey(userId, "medical_fee"));
		window.localStorage.removeItem(chatStorageKey(userId, "facility_standard"));
	} catch {
		// ignore
	}
};

const isDesktop = () => {
	if (typeof window === "undefined") return false;
	return window.matchMedia?.("(min-width: 640px)")?.matches ?? window.innerWidth >= 640;
};

const closedThisSessionKey = (userId: string) => `shirobon_ai_widget_closed_session:${userId}`;

const markClosedThisSession = (userId: string) => {
	if (typeof window === "undefined") return;
	try {
		window.sessionStorage.setItem(closedThisSessionKey(userId), "1");
	} catch {
		// ignore
	}
};

export function FloatingWidget({
	apiUrl,
	userId,
	mode,
	position = "bottom-right",
	zIndex = 10000,
	defaultOpen,
}: FloatingWidgetProps) {
	const pageModeHint = detectPageModeHint();
	// URL-based default unless caller explicitly sets mode.
	const initialMode: Mode = mode ?? pageModeHint;
	const restoredUi = safeLoadJson<PersistedUiStateV1>(uiStorageKey(userId));
	// PC is always open by default; allow caller to force-open with defaultOpen=true,
	// but do not allow forcing closed (defaultOpen=false) since discovery is a top priority.
	const desktop = isDesktop();
	const effectiveDefaultOpen = defaultOpen === true ? true : desktop;
	// Discovery first: on desktop we always start opened.
	const shouldForceOpenOnThisLoad = desktop;
	const initialIsOpen =
		shouldForceOpenOnThisLoad
			? true
			: restoredUi?.v === 1
				? restoredUi.isOpen
			: effectiveDefaultOpen; // PCはデフォルトで開く（スマホは現状どおり閉じ）
	const initialHasInitialized =
		shouldForceOpenOnThisLoad
			? true
			: restoredUi?.v === 1
				? restoredUi.hasInitialized
			: effectiveDefaultOpen;
	const initialUiMode =
		mode
			? initialMode
			: initialMode; // always prefer URL hint when mode isn't explicitly provided

	const [isOpen, setIsOpen] = useState(initialIsOpen);
	const [currentMode, setCurrentMode] = useState<Mode>(initialUiMode);
	// Key to force remount of WidgetChatContainer (resets chat state)
	const [chatKey, setChatKey] = useState(0);
	// Track if chat has been initialized (to keep it mounted when minimized)
	const [hasInitialized, setHasInitialized] = useState(initialHasInitialized);

	const persistUi = useCallback((next: Partial<PersistedUiStateV1>) => {
		const payload: PersistedUiStateV1 = {
			v: 1,
			isOpen: next.isOpen ?? isOpen,
			hasInitialized: next.hasInitialized ?? hasInitialized,
			currentMode: next.currentMode ?? currentMode,
		};
		safeSaveJson(uiStorageKey(userId), payload);
	}, [userId, isOpen, hasInitialized, currentMode]);

	const handleOpen = useCallback(() => {
		setIsOpen(true);
		setHasInitialized(true);
		persistUi({ isOpen: true, hasInitialized: true });
	}, [persistUi]);

	// 最小化: 会話を保持したまま閉じる（コンポーネントはマウントしたまま非表示）
	const handleMinimize = useCallback(() => {
		setIsOpen(false);
		// hasInitializedはtrueのままなのでコンポーネントは維持される
		markClosedThisSession(userId);
		persistUi({ isOpen: false });
	}, [userId, persistUi]);

	// 閉じる: 会話をリセットして閉じる
	const handleReset = useCallback(() => {
		setIsOpen(false);
		setHasInitialized(false);
		markClosedThisSession(userId);
		clearPersistedState(userId);
		// Reset chat by incrementing key (forces remount on next open)
		setChatKey((prev) => prev + 1);
		persistUi({ isOpen: false, hasInitialized: false });
	}, [userId, persistUi]);

	// モード切替: 会話をリセットして新しいモードで開始
	const handleModeChange = useCallback((newMode: Mode) => {
		if (newMode === currentMode) return;
		setCurrentMode(newMode);
		setChatKey((prev) => prev + 1); // 会話をリセット
		persistUi({ currentMode: newMode });
	}, [currentMode, persistUi]);

	const positionClasses = position === "bottom-right" 
		? "right-4 sm:right-6" 
		: "left-4 sm:left-6";

	// モードに応じたサブタイトル
	const subtitle = currentMode === "medical_fee" 
		? "医科診療報酬Q&A" 
		: "施設基準Q&A";

	return (
		<div
			className="shirobon-ai-widget-root"
			style={{ 
				position: "fixed",
				bottom: "16px",
				zIndex,
			}}
			data-position={position}
		>
			{/* Toggle Button - shown when closed */}
			{!isOpen && (
				<button
					type="button"
					onClick={handleOpen}
					className={cn(
						// Use the same rounded shape as the icon to avoid a circular "halo" behind the rounded-square logo.
						"fixed bottom-4 sm:bottom-6 flex items-center justify-center rounded-2xl shadow-lg transition-all hover:shadow-xl hover:scale-105 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2",
						positionClasses,
					)}
					style={{ zIndex, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer', width: '72px', height: '72px' }}
					aria-label="しろぼんAIを開く"
				>
					<ShirobonAIIcon size={72} />
				</button>
			)}

			{/* Chat Panel - keep mounted when hasInitialized, but hide when not open */}
			{hasInitialized && (
				<div
					className={cn(
						"fixed bottom-4 sm:bottom-6 flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl transition-all duration-200",
						positionClasses,
						// Responsive sizing
						"w-[calc(100vw-32px)] sm:w-[420px]",
						"h-[calc(100vh-100px)] sm:h-[600px] max-h-[700px]",
						// Hide when minimized
						isOpen ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none",
					)}
					style={{ zIndex }}
				>
					{/* Header */}
					<div className="flex-shrink-0 border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-white">
						<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px' }}>
							{/* Left: Icon + Title */}
							<div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
								<ShirobonAIIcon size={36} style={{ borderRadius: '8px' }} />
								<div>
									<div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', lineHeight: 1.2 }}>しろぼんAI</div>
									<div style={{ fontSize: '11px', color: '#64748b', marginTop: '1px' }}>{subtitle}</div>
								</div>
							</div>

							{/* Center: Tabs */}
							<div style={{ display: 'inline-flex', flexDirection: 'row', alignItems: 'center', borderRadius: '6px', border: '1px solid #e2e8f0', backgroundColor: '#f1f5f9', padding: '2px', fontSize: '11px', fontWeight: 500, marginLeft: '8px', marginRight: '8px' }}>
								<button
									type="button"
									style={{
										margin: 0,
										borderRadius: '4px',
										padding: '3px 8px',
										transition: 'all 0.15s',
										backgroundColor: currentMode === "medical_fee" ? '#ffffff' : 'transparent',
										color: currentMode === "medical_fee" ? '#047857' : '#64748b',
										boxShadow: currentMode === "medical_fee" ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
										border: 'none',
										cursor: 'pointer',
									}}
									onClick={() => handleModeChange("medical_fee")}
								>
									医科
								</button>
								<button
									type="button"
									style={{
										margin: 0,
										borderRadius: '4px',
										padding: '3px 8px',
										transition: 'all 0.15s',
										backgroundColor: currentMode === "facility_standard" ? '#ffffff' : 'transparent',
										color: currentMode === "facility_standard" ? '#047857' : '#64748b',
										boxShadow: currentMode === "facility_standard" ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
										border: 'none',
										cursor: 'pointer',
									}}
									onClick={() => handleModeChange("facility_standard")}
								>
									施設基準
								</button>
							</div>

							{/* Right: Buttons */}
							<div style={{ display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
								{/* 最小化ボタン */}
								<button
									type="button"
									onClick={handleMinimize}
									className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
									style={{ margin: 0 }}
									aria-label="最小化"
									title="最小化（会話を保持）"
								>
									<ChevronDown style={{ width: '18px', height: '18px', strokeWidth: 2.5, display: 'block' }} />
								</button>
								{/* 閉じるボタン（リセット） */}
								<button
									type="button"
									onClick={handleReset}
									className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500"
									style={{ margin: 0 }}
									aria-label="閉じる"
									title="閉じる（会話をリセット）"
								>
									<X style={{ width: '16px', height: '16px', strokeWidth: 2.5, display: 'block' }} />
								</button>
							</div>
						</div>
					</div>

					{/* Chat Content */}
					<div className="flex-1 overflow-hidden">
						<WidgetChatContainer
							key={chatKey}
							apiUrl={apiUrl}
							userId={userId}
							mode={currentMode}
						/>
					</div>
				</div>
			)}
		</div>
	);
}

